import { useState, useEffect, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useApiCall } from "@/_common/hooks/actions/common/appApiCallHook";
import { SAGA_ACTIONS, URLS } from "@/_config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface LocationState {
  email?: string;
}

const RESEND_COOLDOWN = 60;

export default function VerifyOtp() {
  const location = useLocation<LocationState>();
  const email = location.state?.email ?? "";

  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const callApi = useApiCall();
  const history = useHistory();

  // Guard: if no email in state, send back to login
  if (!email) {
    history.replace(URLS.AUTH.LOGIN);
    return null;
  }

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!otp || otp.length !== 6) {
      setError("Please enter the 6-digit code sent to your email.");
      return;
    }

    setLoading(true);
    callApi(
      SAGA_ACTIONS.ADMIN.AUTH.VERIFY_OTP,
      { email, otp },
      () => {
        setLoading(false);
        history.push(URLS.DASHBOARD.HOME);
      },
      (msg: string) => {
        setLoading(false);
        setError(msg || "Invalid or expired OTP. Please try again.");
      }
    );
  };

  const handleResend = () => {
    if (cooldown > 0 || resendLoading) return;
    setResendLoading(true);
    setError("");
    callApi(
      SAGA_ACTIONS.ADMIN.AUTH.RESEND_OTP,
      { email },
      (msg: string) => {
        setResendLoading(false);
        setOtp("");
        toast.success(msg || "A new code has been sent to your email.");
        startCooldown();
      },
      (msg: string) => {
        setResendLoading(false);
        toast.error(msg || "Failed to resend OTP. Please try again.");
      }
    );
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold text-2xl mb-4">
            D
          </div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">
            Dr.Job <span className="text-primary">Admin</span>
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Two-Factor Authentication</p>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
          <p className="text-sm text-muted-foreground mb-4">
            A 6-digit verification code was sent to{" "}
            <span className="font-medium text-foreground">{email}</span>. Enter it below to continue.
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="text-sm font-medium text-foreground mb-1.5 block">Verification Code</label>
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, "");
                  setOtp(val);
                  if (error) setError("");
                }}
                autoFocus
                className="text-center tracking-widest text-lg"
              />
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
                {error}
              </p>
            )}

            <Button type="submit" className="w-full mt-1" disabled={loading}>
              {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : "Verify & Sign In"}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <span className="text-sm text-muted-foreground">Didn't receive the code? </span>
            {cooldown > 0 ? (
              <span className="text-sm text-muted-foreground">Resend in {cooldown}s</span>
            ) : (
              <button
                type="button"
                onClick={handleResend}
                disabled={resendLoading}
                className="text-sm text-primary underline underline-offset-2 hover:opacity-80 transition-opacity disabled:opacity-50"
              >
                {resendLoading ? "Sending..." : "Resend code"}
              </button>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-4">
          <button
            type="button"
            className="underline underline-offset-2 hover:text-foreground transition-colors"
            onClick={() => history.push(URLS.AUTH.LOGIN)}
          >
            Back to login
          </button>
        </p>

        <p className="text-center text-xs text-muted-foreground mt-4">
          DrJobs Admin Panel &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
